
<template>
  <q-card class="no-padding bg-white no-scroll full-height">
    <q-card-main class="row full-height no-padding">
      <div class="col-12 dash-number-box dash-number-box-padding relative-position">
       <div style="position: absolute; top: 10px; right: 10px; display: flex; align-items: center;">
          <q-select
            v-show="showAverage"
            v-model="averageYear"
            :placeholder="$t('year')"
            :options="averageYearOptions"
            class="q-mr-sm"
            no-parent-field
            hide-underline
          />

          <q-toggle
            v-model="showAverage"
          >
            <q-tooltip :offset="[0, 5]">{{ showAverage ? $t('hide_yearly_average') : $t('show_yearly_average') }}</q-tooltip>
          </q-toggle>
        </div>

        <h2 class="dash-number-box__heading--bold">{{ $t('statistics_expenses') }}</h2>
        <h3 class="dash-number-box__sub-heading">
          {{ $t('statistics_activity_from_to', { date1: expensesFrom, date2: expensesTo }) }}
        </h3>

        <div class="row items-start">
          <span class="dash-number-box__value on-left">{{ formatMoney(lastExpenses, currency, 0, 0) }}</span>
          <trend-hint v-if="showTrend" class="dash-number-box__value--trend" :up="!expensesIsUp" :text="trendText"/>
        </div>
        <span
          class="dash-number-box__value--sub"
          v-html="$t('statistics_expenses_month')"
        />
      </div>

      <sparkline
        :is="chartComponent"
        class="col-12 self-end"
        v-if="expenses"
        :chartdata="expensesChartdata"
        :options="expensesChartOptions"
        :height="100"
      />
    </q-card-main>
  </q-card>
</template>

<script>
  import { formatMoney, formatPercentage } from 'src/utils/format'
  import { mapActions, mapGetters } from 'vuex'
  import Decimal from 'decimal.js'
  import Sparkline from 'components/Charts/Sparkline.vue'
  import SparklineWithAverage from 'components/Charts/SparklineWithAverage.vue'
  import TrendHint from 'components/Charts/TrendHint.vue'
  import { date } from 'quasar'
  import parseDate from 'date-fns/parse'

  export default {
    props: {
      expenses: {
        type: Array,
        required: true
      },
      currency: {
        type: String,
        required: true
      },
      average: {
        type: Object,
        required: true
      }
    },
    computed: {
      ...mapGetters(['activeTeam', 'showDashboardExpensesAverage']),
      chartComponent () {
        return this.showAverage ? 'SparklineWithAverage' : 'Sparkline'
      },
      expensesFrom () {
        return date.formatDate(parseDate(this.expenses[0].start_date), 'MMM YYYY')
      },
      expensesTo () {
        return date.formatDate(parseDate(this.expenses[this.expenses.length - 1].end_date), 'MMM YYYY')
      },
      lastExpenses () {
        return this.expenses[this.expenses.length - 1].value
      },
      secondLastExpenses () {
        return this.expenses[this.expenses.length - 2].value
      },
      lastSecondLastExpensesDifference () {
        return Math.abs(this.lastExpenses - this.secondLastExpenses)
      },
      showTrend () {
        return this.secondLastExpenses !== 0
      },
      expensesIsUp () {
        return this.lastExpenses > this.secondLastExpenses
      },
      growth () {
        return new Decimal(this.lastSecondLastExpensesDifference)
          .div(new Decimal(this.secondLastExpenses))
      },
      trendText () {
        if (!this.showTrend) {
          return null
        }

        if (this.growth.eq(0)) {
          return '+0%'
        }

        const growthPercentage = formatPercentage(this.growth, 0, 1)
        const sign = this.expensesIsUp ? '+' : '-'

        return `${sign}${growthPercentage} (${formatMoney(this.lastSecondLastExpensesDifference, this.currency, 0, 0)})`
      },
      expensesChartdata () {
        if (!this.expenses) {
          return {}
        }

        return {
          labels: this.expenses.map((entry) => date.formatDate(parseDate(entry.start_date), 'MMMM YYYY')),
          datasets: [{
            label: this.$tc('expense', 2),
            data: this.expenses.map((entry) => entry.value),
            borderColor: '#f27213',
            backgroundColor: '#fef6f0',
            pointBorderColor: '#f27213',
            pointBackgroundColor: '#fef6f0',
            fill: true
          }]
        }
      },
      expensesChartOptions () {
        return {
          average: {
            averageLineColor: '#d53f8c',
            averageLabelColor: '#1a202c',
            averageLabelFunction: (average) => formatMoney(average, this.currency, 0, 0),
            averageValue: this.average ? this.average.value : -1
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                return formatMoney(tooltipItem.yLabel)
              }
            }
          }
        }
      },
      showAverage: {
        get () {
          return this.showDashboardExpensesAverage
        },

        set (value) {
          this.setShowDashboardExpensesAverage(value)
        }
      },
      averageYearOptions () {
        const currentYear = new Date().getFullYear()
        const teamCreatedAt = this.activeTeam ? parseDate(this.activeTeam.created_at.date, this.$t('date_time_format')).getFullYear() : currentYear

        const values = []

        for (let i = teamCreatedAt; i <= currentYear; i++) {
          values.push({ label: i.toString(), value: i })
        }

        return values
      },
      averageYear: {
        get () {
          return this.average.options ? this.average.options.year : undefined
        },

        set (value) {
          this.$emit('averageYearChanged', value)
        }
      }
    },
    methods: {
      ...mapActions(['setShowDashboardExpensesAverage']),
      formatMoney: formatMoney
    },
    components: {
      Sparkline,
      SparklineWithAverage,
      TrendHint
    }
  }
</script>
